import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SkillReplay from './SkillReplay';
import ObservationControls from './ObservationControls';

const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

// ── Types ──────────────────────────────────────────────────────────
interface SkillStep {
  id: string;
  order: number;
  action: string;
  label?: string;
  config: string;
  requiresConfirmation: boolean;
  guardianGate: boolean;
}

interface Skill {
  id: string;
  name: string;
  description?: string;
  category: string;
  icon: string;
  triggerPhrase?: string;
  confidence: number;
  timesExecuted: number;
  successRate: number;
  status: string;
  source: string;
  steps: SkillStep[];
  createdAt: string;
}

interface SkillStats {
  totalSkills: number;
  activeSkills: number;
  categories: {
    category: string;
    icon: string;
    count: number;
    avgConfidence: number;
    totalExecutions: number;
  }[];
}

interface Props {
  replicaId: string;
  replicaName?: string;
  onBack?: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const CATEGORY_COLORS: Record<string, string> = {
  communication: '#6366f1',
  finance: '#22c55e',
  scheduling: '#f59e0b',
  research: 'var(--accent)',
  document: '#8b5cf6',
  writing: '#F472B6',
  custom: '#64748b',
  general: '#64748b',
};

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  draft: { bg: '#f59e0b20', text: '#f59e0b', label: 'Draft' },
  active: { bg: '#22c55e20', text: '#22c55e', label: 'Active' },
  archived: { bg: '#64748b20', text: '#64748b', label: 'Archived' },
  failed: { bg: '#ef444420', text: '#ef4444', label: 'Failed' },
  system: { bg: '#F472B620', text: '#F472B6', label: 'System' },
};

// ── System Skills (built-in, read-only) ──────────────────────────────
const SYSTEM_SKILLS: Skill[] = [
  {
    id: 'system_stop_slop',
    name: 'Stop Slop — Writing Rules',
    description: 'Eliminates predictable AI writing patterns. Bans filler phrases, adverbs, rhetorical setups, and metronomic rhythm. Enforces directness, varied sentence structure, and reader trust.',
    category: 'writing',
    icon: '✍️',
    triggerPhrase: 'write clean',
    confidence: 1,
    timesExecuted: 0,
    successRate: 1,
    status: 'system',
    source: 'system',
    steps: [
      { id: 'ss_1', order: 1, action: 'filter', label: 'Strip banned phrases', config: '{}', requiresConfirmation: false, guardianGate: false },
      { id: 'ss_2', order: 2, action: 'filter', label: 'Remove adverbs', config: '{}', requiresConfirmation: false, guardianGate: false },
      { id: 'ss_3', order: 3, action: 'filter', label: 'Fix banned structures', config: '{}', requiresConfirmation: false, guardianGate: false },
      { id: 'ss_4', order: 4, action: 'decide', label: 'Score prose 1–10 × 5 dimensions', config: '{}', requiresConfirmation: false, guardianGate: false },
      { id: 'ss_5', order: 5, action: 'decide', label: 'Revise if below 35/50', config: '{}', requiresConfirmation: false, guardianGate: false },
    ],
    createdAt: '2026-03-20T00:00:00Z',
  },
];

// ── Main Component ────────────────────────────────────────────────
export const SkillLibrary: React.FC<Props> = ({ replicaId, replicaName = 'Replica', onBack }) => {
  const { user } = useAuth();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [stats, setStats] = useState<SkillStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [executing, setExecuting] = useState<string | null>(null);
  const [showReplay, setShowReplay] = useState<{ skillId: string; skillName: string } | null>(null);
  const [showObservation, setShowObservation] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load ───────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const authToken = await getToken(user);
      const [skillsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills/stats`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);
      const [skillsData, statsData] = await Promise.all([skillsRes.json(), statsRes.json()]);
      if (skillsData.success) setSkills(skillsData.skills || []);
      if (statsData.success) setStats(statsData.stats);
    } catch (e) {
      console.error('Failed to load skills:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, user]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Execute Skill ──────────────────────────────────────────────
  const handleExecute = async (skill: Skill) => {
    console.log('🔵 handleExecute called for:', skill.name, skill.id, 'source:', skill.source, 'status:', skill.status);
    // System skills are built-in and always-active; they cannot be executed via API
    if (skill.source === 'system') {
      showToast('System skills are always active and execute automatically when triggered in chat.', 'info');
      return;
    }
    try {
      const authToken = await getToken(user);
      setExecuting(skill.id);
      showToast(`▶ Executing "${skill.name}"...`, 'info');
      const res = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills/${skill.id}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: '{}',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        showToast(`❌ ${data.error || 'Execution failed'}`, 'error');
        return;
      }
      // Auto-step through execution
      let done = false;
      let stepCount = 0;
      while (!done && stepCount < 50) {
        stepCount++;
        const stepRes = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/executions/${data.executionId}/step`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const stepData = await stepRes.json();
        if (!stepData.success || stepData.execution?.status === 'completed' || stepData.needsApproval) {
          done = true;
        }
      }
      showToast(`✅ "${skill.name}" completed successfully`, 'success');
      loadData();
    } catch (e: any) {
      console.error('Execution failed:', e);
      showToast(`❌ Execution failed: ${e.message || 'Network error'}`, 'error');
    } finally {
      setExecuting(null);
    }
  };

  // ── Activate Skill ─────────────────────────────────────────────
  const handleActivate = async (skillId: string, skillName: string) => {
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills/${skillId}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        showToast(`✅ "${skillName}" activated`, 'success');
      } else {
        showToast('❌ Failed to activate skill', 'error');
      }
      loadData();
    } catch (e) {
      console.error('Activation failed:', e);
      showToast('❌ Activation failed', 'error');
    }
  };

  // ── Archive Skill ──────────────────────────────────────────────
  const handleArchive = async (skillId: string, skillName: string) => {
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills/${skillId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        showToast(`"${skillName}" archived`, 'info');
      } else {
        showToast('❌ Failed to archive skill', 'error');
      }
      setSelectedSkill(null);
      loadData();
    } catch (e) {
      console.error('Archive failed:', e);
      showToast('❌ Archive failed', 'error');
    }
  };

  // ── Merge system skills + filter ──────────────────────────────
  const allSkills = [...SYSTEM_SKILLS, ...skills];
  const filteredSkills = filterCategory === 'all'
    ? allSkills
    : allSkills.filter(s => s.category === filterCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900">
      {/* ── Command Strip ──────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white text-sm transition-colors">
            ← Back
          </button>
          <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />
          <div className="flex items-center gap-2">
            <span className="text-lg">⚡</span>
            <h1 className="text-base font-bold text-slate-900 dark:text-white">Skill Library</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{stats.totalSkills} skills</span>
              <span>•</span>
              <span className="text-green-400">{stats.activeSkills} active</span>
            </div>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-slate-900 dark:text-white text-xs font-semibold rounded-lg transition-colors"
          >
            + Teach Skill
          </button>
        </div>
      </div>

      {/* ── Category Stats ─────────────────────────────────────── */}
      {stats && stats.categories.length > 0 && (
        <div className="flex gap-2 px-6 py-3 border-b border-slate-200 dark:border-slate-700/30 bg-white/20 dark:bg-slate-800/20 overflow-x-auto">
          <button
            onClick={() => setFilterCategory('all')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
              filterCategory === 'all' ? 'bg-indigo-600 text-slate-900 dark:text-white' : 'bg-slate-100/50 dark:bg-slate-700/30 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white'
            }`}
          >
            All ({stats.totalSkills})
          </button>
          {stats.categories.map(cat => (
            <button
              key={cat.category}
              onClick={() => setFilterCategory(cat.category)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
                filterCategory === cat.category ? 'bg-indigo-600 text-slate-900 dark:text-white' : 'bg-slate-100/50 dark:bg-slate-700/30 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white'
              }`}
            >
              <span>{cat.icon}</span>
              <span className="capitalize">{cat.category}</span>
              <span className="opacity-60">({cat.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Skill Grid */}
        <div className={`flex-1 p-6 overflow-auto ${selectedSkill ? 'pr-0' : ''}`}>
          {filteredSkills.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <span className="text-4xl mb-3">⚡</span>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">No Skills Yet</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
                Teach {replicaName} by creating a skill with step-by-step actions.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-slate-900 dark:text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Teach First Skill
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSkills.map(skill => {
                const statusStyle = STATUS_STYLES[skill.status] || STATUS_STYLES.draft;
                const catColor = CATEGORY_COLORS[skill.category] || CATEGORY_COLORS.custom;
                const isRunning = executing === skill.id;

                return (
                  <div
                    key={skill.id}
                    onClick={() => setSelectedSkill(skill)}
                    className={`bg-white/50 dark:bg-slate-800/50 border rounded-2xl p-5 cursor-pointer transition-all hover:border-indigo-500/30 hover:bg-white dark:bg-slate-800/70 ${
                      selectedSkill?.id === skill.id ? 'border-indigo-500/50 ring-1 ring-indigo-500/20' : 'border-slate-200 dark:border-slate-700/50'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{skill.icon}</span>
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white">{skill.name}</h3>
                          <span className="text-xs capitalize" style={{ color: catColor }}>{skill.category}</span>
                        </div>
                      </div>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                      >
                        {statusStyle.label}
                      </span>
                    </div>

                    {/* Description */}
                    {skill.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">{skill.description}</p>
                    )}

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                      <div className="flex items-center gap-3">
                        <span>{skill.steps.length} steps</span>
                        <span>•</span>
                        <span>{skill.timesExecuted} runs</span>
                        {skill.successRate > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-green-400">{Math.round(skill.successRate * 100)}%</span>
                          </>
                        )}
                      </div>
                      {/* Confidence bar */}
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${skill.confidence * 100}%`,
                              backgroundColor: catColor,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700/30" style={{ position: 'relative', zIndex: 10 }}>
                      {skill.status === 'system' && (
                        <span className="flex-1 px-3 py-1.5 text-center text-xs font-medium text-pink-300/70">
                          Built-in • Always active
                        </span>
                      )}
                      {skill.status === 'active' && (
                        <button
                          onClick={e => { e.stopPropagation(); e.preventDefault(); handleExecute(skill); }}
                          disabled={isRunning}
                          className="flex-1 px-3 py-1.5 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-xs font-medium rounded-lg hover:bg-indigo-600/30 transition-colors disabled:opacity-50"
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                          {isRunning ? '⏳ Running...' : '▶ Execute'}
                        </button>
                      )}
                      {skill.status === 'draft' && (
                        <button
                          onClick={e => { e.stopPropagation(); e.preventDefault(); handleActivate(skill.id, skill.name); }}
                          className="flex-1 px-3 py-1.5 bg-green-600/20 border border-green-500/30 text-green-300 text-xs font-medium rounded-lg hover:bg-green-600/30 transition-colors"
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                          ✅ Activate
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedSkill && (
          <div className="w-80 border-l border-slate-200 dark:border-slate-700/50 bg-white/20 dark:bg-slate-800/20 p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Skill Details</h3>
              <button onClick={() => setSelectedSkill(null)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white text-xs">
                ✕
              </button>
            </div>

            {/* Info */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{selectedSkill.icon}</span>
                <div>
                  <h4 className="text-base font-bold text-slate-900 dark:text-white">{selectedSkill.name}</h4>
                  <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{selectedSkill.source} • {selectedSkill.category}</span>
                </div>
              </div>
              {selectedSkill.description && (
                <p className="text-xs text-slate-700 dark:text-slate-300">{selectedSkill.description}</p>
              )}
              {selectedSkill.triggerPhrase && (
                <div className="bg-slate-100/50 dark:bg-slate-700/30 rounded-lg px-3 py-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500">Trigger:</span>
                  <p className="text-xs text-indigo-300 font-mono">"{selectedSkill.triggerPhrase}"</p>
                </div>
              )}
            </div>

            {/* Steps */}
            <div className="mb-4">
              <h5 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Steps ({selectedSkill.steps.length})</h5>
              <div className="space-y-2">
                {selectedSkill.steps.map((step, i) => (
                  <div key={step.id} className="flex items-start gap-2 text-xs">
                    <span className="w-5 h-5 flex items-center justify-center bg-slate-100 dark:bg-slate-700/50 rounded-full text-slate-500 dark:text-slate-400 shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <span className="text-slate-900 dark:text-white font-medium">{step.label || step.action}</span>
                      <div className="flex gap-1 mt-0.5">
                        {step.requiresConfirmation && (
                          <span className="text-yellow-400 text-xs">🔒 confirm</span>
                        )}
                        {step.guardianGate && (
                          <span className="text-red-400 text-xs">🛡️ guardian</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-slate-50 dark:bg-slate-700/20 rounded-lg px-3 py-2 text-center">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{selectedSkill.timesExecuted}</span>
                <span className="block text-xs text-slate-400 dark:text-slate-500">Executions</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/20 rounded-lg px-3 py-2 text-center">
                <span className="text-lg font-bold text-green-400">{Math.round(selectedSkill.successRate * 100)}%</span>
                <span className="block text-xs text-slate-400 dark:text-slate-500">Success</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/20 rounded-lg px-3 py-2 text-center">
                <span className="text-lg font-bold text-indigo-400">{Math.round(selectedSkill.confidence * 100)}%</span>
                <span className="block text-xs text-slate-400 dark:text-slate-500">Confidence</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/20 rounded-lg px-3 py-2 text-center">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{selectedSkill.steps.length}</span>
                <span className="block text-xs text-slate-400 dark:text-slate-500">Steps</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              {selectedSkill.source === 'system' && (
                <div className="text-center py-2 text-xs text-pink-300/70 bg-pink-500/5 rounded-xl border border-pink-500/10">
                  ✍️ System skill — always active, read-only
                </div>
              )}
              {selectedSkill.source !== 'system' && selectedSkill.status === 'active' && (
                <button
                  onClick={() => handleExecute(selectedSkill)}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-slate-900 dark:text-white text-xs font-semibold rounded-xl transition-colors"
                >
                  ▶ Execute Now
                </button>
              )}
              {selectedSkill.source !== 'system' && selectedSkill.status === 'draft' && (
                <button
                  onClick={() => handleActivate(selectedSkill.id, selectedSkill.name)}
                  className="w-full py-2 bg-green-600 hover:bg-green-500 text-slate-900 dark:text-white text-xs font-semibold rounded-xl transition-colors"
                >
                  ✅ Activate Skill
                </button>
              )}
              {selectedSkill.source !== 'system' && (
                <button
                  onClick={() => handleArchive(selectedSkill.id, selectedSkill.name)}
                  className="w-full py-2 border border-slate-300 dark:border-slate-600/50 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white text-xs rounded-xl transition-colors"
                >
                  Archive
                </button>
              )}
              {selectedSkill.timesExecuted > 0 && (
                <button
                  onClick={() => setShowReplay({ skillId: selectedSkill.id, skillName: selectedSkill.name })}
                  className="w-full py-2 bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs font-semibold rounded-xl hover:bg-violet-600/30 transition-colors"
                >
                  🔄 View Replay
                </button>
              )}
            </div>

            {/* Observation Controls */}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/30">
              <button
                onClick={() => setShowObservation(!showObservation)}
                className="w-full flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors mb-2"
              >
                <span className="font-medium">👁️ Observation Mode</span>
                <span>{showObservation ? '▴' : '▾'}</span>
              </button>
              {showObservation && (
                <ObservationControls
                  replicaId={replicaId}
                  replicaName={replicaName}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Teach Skill Modal ──────────────────────────────────── */}
      {showCreateModal && (
        <TeachSkillModal
          replicaId={replicaId}
          user={user}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadData(); }}
        />
      )}

      {/* Skill Replay Modal */}
      {showReplay && (
        <SkillReplay
          replicaId={replicaId}
          skillId={showReplay.skillId}
          skillName={showReplay.skillName}
          onClose={() => setShowReplay(null)}
        />
      )}

      {/* ── Toast Notification ──────────────────────────────────── */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            padding: '10px 20px',
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            background: toast.type === 'success' ? 'rgba(34,197,94,0.9)'
              : toast.type === 'error' ? 'rgba(239,68,68,0.9)'
              : 'rgba(99,102,241,0.9)',
            animation: 'fadeInUp 0.25s ease-out',
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ── Teach Skill Modal ─────────────────────────────────────────────
const TeachSkillModal: React.FC<{
  replicaId: string;
  user: any;
  onClose: () => void;
  onCreated: () => void;
}> = ({ replicaId, user, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('communication');
  const [triggerPhrase, setTriggerPhrase] = useState('');
  const [steps, setSteps] = useState([{ action: 'send_email', label: '', guardianGate: false }]);
  const [saving, setSaving] = useState(false);

  const addStep = () => setSteps([...steps, { action: 'send_email', label: '', guardianGate: false }]);
  const removeStep = (i: number) => setSteps(steps.filter((_, idx) => idx !== i));

  const handleCreate = async () => {
    if (!name.trim() || steps.length === 0) return;
    setSaving(true);
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/skills/${replicaId}/skills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          name, description, category, triggerPhrase: triggerPhrase || undefined,
          source: 'taught',
          steps: steps.map(s => ({
            action: s.action,
            label: s.label || undefined,
            guardianGate: s.guardianGate,
          })),
        }),
      });
      if ((await res.json()).success) onCreated();
    } catch (e) {
      console.error('Failed to create skill:', e);
    } finally {
      setSaving(false);
    }
  };

  const ACTIONS = ['send_email', 'create_doc', 'search', 'summarize', 'ask_human', 'wait', 'decide', 'notify', 'api_call', 'transform_data'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600/50 rounded-2xl w-[520px] max-h-[85vh] overflow-y-auto p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">⚡ Teach New Skill</h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Skill Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Draft Client Email"
              className="w-full bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this skill do?"
              className="w-full h-16 bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                {['communication', 'finance', 'scheduling', 'research', 'document', 'custom'].map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Trigger Phrase</label>
              <input value={triggerPhrase} onChange={e => setTriggerPhrase(e.target.value)} placeholder="optional"
                className="w-full bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
            </div>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Steps</label>
              <button onClick={addStep} className="text-xs text-indigo-400 hover:text-indigo-300">+ Add Step</button>
            </div>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/20 rounded-lg p-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500 w-5">{i + 1}.</span>
                  <select value={step.action} onChange={e => { const n = [...steps]; n[i].action = e.target.value; setSteps(n); }}
                    className="flex-1 bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded px-2 py-1 text-xs text-slate-900 dark:text-white">
                    {ACTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                  </select>
                  <input value={step.label} onChange={e => { const n = [...steps]; n[i].label = e.target.value; setSteps(n); }}
                    placeholder="Label" className="w-24 bg-slate-100 dark:bg-slate-700/50 border border-slate-300 dark:border-slate-600/50 rounded px-2 py-1 text-xs text-slate-900 dark:text-white placeholder-slate-500" />
                  <label className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                    <input type="checkbox" checked={step.guardianGate}
                      onChange={e => { const n = [...steps]; n[i].guardianGate = e.target.checked; setSteps(n); }}
                      className="accent-red-500" /> 🛡️
                  </label>
                  {steps.length > 1 && (
                    <button onClick={() => removeStep(i)} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors">Cancel</button>
          <button onClick={handleCreate} disabled={!name.trim() || saving}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-slate-900 dark:text-white text-sm font-semibold rounded-xl transition-colors">
            {saving ? 'Creating...' : 'Create Skill'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillLibrary;
